import { z } from 'zod';
import { DateString, JsonObject, Timestamp, UserRef, Uuid } from './common.js';
import {
  PROFILE_LINK_ACTIONS,
  PROFILE_LINK_PLATFORMS,
  AdjudicationSensitivity,
  DecisionActor,
  DecisionKind,
  DecisionStatus,
  FactType,
  MatchKind,
  MoreProseOrigin,
  OrgRelation,
  PendingDecisionKind,
  Severity,
  SourceTier,
} from './vocab.js';

/** Re-exported from `vocab.ts`, where the closed vocabularies live. */
export { MORE_PROSE_ORIGINS, MoreProseOrigin } from './vocab.js';

/**
 * Decision payloads and answers — the candidate-fork UX rides here
 * (server-design.md §3.4, agent-architecture.md §3.3).
 *
 * HARD RULE, enforced by these schemas: no numeric score, confidence, rank or
 * percentage appears in any payload. The server stores internal score features
 * on the decision row; this projection is the allow-list that keeps them in.
 * Candidates arrive **server-ordered** — the client never re-sorts and never
 * derives an order of its own.
 */

/* ————————————————————————— identity payload parts ————————————————————————— */

/**
 * One anchor → evidence pairing behind a candidate. `near_match` carries the
 * "did you mean Meritage?" question: `original` is what the source said,
 * `suggested` is the repaired anchor value an `anchor_corrections[]` entry would
 * write.
 *
 * `evidence_id` is null for a pairing sourced from a registry record
 * (`person_records`) rather than a fetched page: the merger strips the key
 * because there is no evidence row to cite, and the server projects the absent
 * key to null. Requiring a GUID here 500'd every ask whose retained list held a
 * records-sourced candidate (2026-09-17). Nothing on the dashboard reads this
 * id today; it is carried for provenance.
 */
export const MatchBasisItem = z.object({
  anchor: z.string(),
  match_kind: MatchKind,
  evidence_id: Uuid.nullable(),
  /** The source's own words that agreed: the agent's `span`, the stub's `excerpt`. */
  excerpt: z.string(),
  /**
   * The detail on the subject's record the source agreed with, as the record
   * holds it ("Baghram and Kandahar, Afghanistan"). The card's plain line. Null
   * on a name row and on a row that names no value.
   */
  matched: z.string().nullable(),
  near_match: z
    .object({
      original: z.string(),
      suggested: z.string(),
    })
    .optional(),
});
export type MatchBasisItem = z.infer<typeof MatchBasisItem>;

/**
 * **Another card on the same list that is probably this person** (2026-10-14).
 * The merger keeps two cards apart unless something identifies them as one, and
 * points at the pairs that share a distinguishing fact anyway. `shared` is what
 * they have in common, `conflicts` what one says that disagrees with the other.
 * A pointer for the office, never a merge: picking one card does not reject a
 * card linked to it.
 */
export const SamePersonLink = z.object({
  candidate_ref: z.string(),
  shared: z.array(z.string()),
  conflicts: z.array(z.string()),
});
export type SamePersonLink = z.infer<typeof SamePersonLink>;

/**
 * **A bindable candidate** — one shape for every list the office can answer
 * from, unified 2026-09-03 with the three ask tiers.
 *
 * It was `IdentityCandidate`, the pick list's own type, while the pick list was
 * the only place a candidate could be chosen. An `over_five` more-prose row now
 * carries a "Select this person" shortcut on each retained row, and a shortcut
 * pick must be indistinguishable downstream from a pick-list pick — so the two
 * lists carry the same candidate, not a rich one and a brief one. `detail_lines`
 * are the card's display lines.
 */
export const Candidate = z.object({
  candidate_ref: z.string(),
  name: z.string(),
  detail_lines: z.array(z.string()),
  match_basis: z.array(MatchBasisItem),
  /** Matched a positive anchor *and* a prior rejection: listed, flagged, positive wins. */
  anti_anchor_collision: z.boolean(),
  /** Empty on a row stored before the merger linked cards, and wherever it linked none. */
  likely_same_as: z.array(SamePersonLink),
});
export type Candidate = z.infer<typeof Candidate>;

/**
 * A candidate the pipeline set aside — the countable collapsed bin, never
 * nothing. Two kinds of set-aside ride here: a prior rejection (`anti_anchor`
 * carries what ruled it out, e.g. `not{44, Tampa FL, orthodontist}`) and, since
 * 2026-09-03, a candidate the pipeline judged impossible on its own — a dead
 * namesake being the case that prompted it — where `reason` is the sentence the
 * office reads and `anti_anchor` may be empty, because nobody rejected anything.
 */
export const SuppressedCandidate = z.object({
  candidate_ref: z.string(),
  name: z.string(),
  anti_anchor: JsonObject,
  /** Why it was set aside, in the pipeline's own words. Rendered verbatim. */
  reason: z.string().optional(),
});
export type SuppressedCandidate = z.infer<typeof SuppressedCandidate>;

/**
 * A candidate rendered read-only, with nothing to press: the `identity_confirm`
 * runner-ups, and nothing else since the retained set became bindable.
 */
export const CandidateBrief = z.object({
  candidate_ref: z.string(),
  name: z.string(),
  detail_lines: z.array(z.string()),
});
export type CandidateBrief = z.infer<typeof CandidateBrief>;

/* ————————————————————————— payloads by kind ————————————————————————— */

export const IdentityPickPayload = z.object({
  candidates: z.array(Candidate),
  suppressed: z.array(SuppressedCandidate),
});
export type IdentityPickPayload = z.infer<typeof IdentityPickPayload>;

/** One on-chain identity offered in an `onchain_entity_pick`. */
export const OnchainEntityCandidate = z.object({
  entity_id: z.string().min(1),
  name: z.string().min(1),
  /** The entity's kind as the source labels it ("individual", "fund"), or null. */
  entity_type: z.string().nullable(),
  /** Its own public links, shown so the office can tell namesakes apart. */
  links: z.array(z.string()),
  /** Why it might be them, in the office's words ("its X account is the one on record"), or null. */
  why: z.string().nullable(),
  /** The one the pipeline would choose: corroborated by a link, never by name alone. */
  recommended: z.boolean(),
});
export type OnchainEntityCandidate = z.infer<typeof OnchainEntityCandidate>;

export const OnchainEntityPickPayload = z.object({
  candidates: z.array(OnchainEntityCandidate).min(1),
  /** How they were found: a search by name, or a wallet the office supplied. */
  found_by: z.enum(['name_search', 'wallet']),
});
export type OnchainEntityPickPayload = z.infer<typeof OnchainEntityPickPayload>;

/** What a recorded `onchain_entity_bind` stores (spec §4.3). */
export const OnchainEntityBindPayload = z.object({
  entity_id: z.string().min(1),
  name: z.string().min(1),
  /** The confirmed wallet whose verified attribution tied the entity. */
  wallet: z.object({ chain: z.string().min(1).nullable(), address: z.string().min(1) }).strict(),
});
export type OnchainEntityBindPayload = z.infer<typeof OnchainEntityBindPayload>;

export const IdentityMoreProsePayload = z.object({
  /** The live candidate count — the number the tier was read off. */
  candidate_count: z.number().int().nonnegative(),
  /** Rendered verbatim: "A city or employer would narrow this fastest." */
  discriminating_anchor_hint: z.string().nullable(),
  /**
   * **Bindable on `over_five`, empty on every other origin.** A retained
   * candidate here can be answered with `{candidate_ref}`, which runs the
   * identity_pick binding path on it (§6.1) — so the held list is a list the
   * office can act on, not a receipt. `over_fifteen` carries none by contract and
   * `candidate_count` is what the prompt says instead; `zero` and
   * `none_of_these` have none to carry.
   */
  retained_candidates: z.array(Candidate),
  /** What the pipeline set aside, countable and collapsed — each with its reason. */
  suppressed: z.array(SuppressedCandidate),
  origin: MoreProseOrigin,
});
export type IdentityMoreProsePayload = z.infer<typeof IdentityMoreProsePayload>;

/**
 * **Past this many, a held list is not a list anybody reads** — the top ask
 * tier's boundary (Soroush, 2026-09-03), named once because three readers
 * enforce it: the server's projection, the server's answer path, and the page.
 */
export const HELD_LIST_MAX = 15;

/**
 * A count as a number, or **zero** — the reading that cannot fail open.
 *
 * `Math.max(NaN, 20)` is `NaN`, and `NaN > 15` is `false`: a payload whose
 * `candidate_count` was not a number (`"many"`, `null`, a missing key read
 * through `Number()`) would have taken twenty retained candidates *past* the
 * ceiling check rather than into it, and the answer path would have bound one
 * off a list the projection was 500ing on. So a count that is not a finite
 * number is not a count, and the list's own length is left to speak.
 */
export const asCount = (value: unknown): number => {
  const count = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
};

/**
 * Is this more-prose row past the held list's ceiling — **read off the count,
 * not only off the label**?
 *
 * A payload that says `over_five` and carries twenty candidates is past the
 * ceiling whatever it calls itself, and the surfaces must agree about that or
 * the page offers a shortcut the server refuses. Deliberately tolerant about
 * where the number comes from: the pipeline's own `candidate_count`, and the
 * length of the list it actually sent, whichever is larger — and each read
 * through `asCount`, so an unusable number counts for nothing instead of
 * poisoning the comparison.
 */
export function exceedsHeldList(
  origin: string,
  candidateCount: unknown,
  retainedCount: unknown = 0,
): boolean {
  return (
    origin === 'over_fifteen' ||
    Math.max(asCount(candidateCount), asCount(retainedCount)) > HELD_LIST_MAX
  );
}

export const IdentityConfirmPayload = z.object({
  bound: z.object({
    candidate_ref: z.string(),
    name: z.string(),
    detail_lines: z.array(z.string()),
    match_basis: z.array(MatchBasisItem),
  }),
  /** They become anti-anchors on confirmation — never before. */
  runner_ups: z.array(CandidateBrief),
});
export type IdentityConfirmPayload = z.infer<typeof IdentityConfirmPayload>;

export const ClaimAdjudicationPayload = z.object({
  claim_id: Uuid,
  fact_type: FactType,
  /**
   * The computed class the question is being asked at (§3c/§3h).
   *
   * `'none'` reaches here by exactly one road: **blocking**. The blocking matrix
   * is misattribution protection and is valence-blind, so a witness litigation
   * bound by name alone at `medium` or `high` still parks the run and still asks
   * — the question is "is this even him", not "how bad is it". What `'none'`
   * never arrives by is the *queued* path: a gated claim the matrix let through
   * with nothing adverse in it is auto-kept and logged as an `auto_keep` event
   * instead of being asked about.
   */
  severity: Severity,
  blocking: z.boolean(),
  adjudication_sensitivity: AdjudicationSensitivity,
  /** Why the system thinks this is him, in the claim's own words — the load-bearing line. */
  subject_binding: z.string(),
  /**
   * **Nullable, because a claim can legitimately have no sentence** (2026-09-10).
   * The extractor may omit both `raw` and the evidence excerpt — a structured
   * source with no quotable span — and ingest stores that as it arrives. The read
   * used to coerce the absence to `''`, and the card then rendered an empty
   * paragraph above the buttons: a question with nothing in it to answer from.
   * `.nullable()` and never `.optional()` (the 2026-09-07 mcp-read-interface
   * note's optional / nullable / nullish audit): the key is always on the wire,
   * and `null` is what "there was no sentence" looks like, where `undefined`
   * would be indistinguishable from an older server. The card falls back to
   * `fields`, the way Flagged findings does.
   */
  evidence_excerpt: z.string().nullable(),
  /**
   * The claim's own `fields`, so the card can write the finding's sentence when
   * there is no excerpt to quote. Read off the claim row at projection time
   * rather than copied into the stored payload, so a decision written before
   * this key existed renders too; `{}` where the claim row is gone.
   */
  fields: JsonObject,
  evidence_id: Uuid.nullable(),
  source_tier: SourceTier,
  content_date: DateString.nullable(),
});
export type ClaimAdjudicationPayload = z.infer<typeof ClaimAdjudicationPayload>;

/**
 * **The auto-keep record** (§3c/§3h) — the pre-resolved half of the gate. A
 * gated type whose computed severity is `none` — today that is a `litigation`
 * naming the subject as a witness or non-party, and only that; the clean records
 * check is sensitive but ungated, so it never gates in the first place — is kept
 * without a question: the fact lands exactly as an ungated one does, and this
 * rides in the `dedup_resolution` payload as the
 * provenance, the way `inherited_exclusion` does for the leave-out guard. It is
 * **not** a `claim_adjudication` row — nothing pends, nothing pauses the run
 * (§3f), and no slot of the run's queued budget is spent. Server-internal like
 * the other `dedup_resolution` payload parts; the office sees it as a line in
 * the decision log, and the fact stays reversible through the normal leave-out.
 */
export const AutoKeepRecord = z
  .object({
    claim_id: Uuid,
    fact_type: FactType,
    /** The class that earned the auto-keep. Always `'none'` today; recorded, not assumed. */
    severity: Severity,
    /** Plain, greppable: `auto_keep: severity none on gated type`. */
    rationale: z.string().min(1),
  })
  .strict();
export type AutoKeepRecord = z.infer<typeof AutoKeepRecord>;

/**
 * **The likely-merge question** (likely-merges note §1b) — two live nodes of one
 * kind that look like the same thing, and the sentence that says why.
 *
 * `kind` is the *entity* kind the pair are both of, not the decision's kind: the
 * question is only ever asked between two people, two organisations or two
 * events, because resolution is scoped `(org, kind, normalized_name)` and a node
 * can never be the same thing as a node of another kind.
 *
 * There is no score on the card. `score` rides here because the operator's
 * duplicates report prints it (`entity:candidates`) and the office's card does
 * not — it reads `reason`, which is the whole of what a person needs to answer.
 */
/**
 * **`same_name` is the fourth, and it is the only one nothing probes for**
 * (organisations-as-subjects note §2 chunk 4). The other three are patterns a
 * report can go looking for over names already on record. This one is a refusal:
 * the resolver was about to bind an organisation reference onto the live record
 * of that name, found the two disagree about *where they are* — a different
 * website, or a different city with no website on either side — and minted the
 * namesake instead of guessing. The question is what it does with the doubt.
 *
 * So the pair's names are **identical**, which is exactly what the other three
 * signals mean the pair to be excluded from: `similar` no longer offers a pair
 * spelled the same way, because "UT and UT are spelled almost the same" is not a
 * sentence anyone can answer. The `reason` here carries the difference instead,
 * and the resolver writes it — no `reasonFor` arm can, because what differs is
 * not in the two names.
 */
/**
 * **`nickname` and `same_employer` are the two people signals** (Phillips gap,
 * 2026-09-10). The three above were built for organisations and they answer
 * people badly: a club's Medici research minted "Clint Phillips" on one run and
 * "Clinton Phillips" on the next, and nothing asked about the pair, because the
 * two names share no whole word order (`contains` is strict on token subsets),
 * carry no acronym, and score **0.78** on trigram — under the 0.8 floor by two
 * hundredths.
 *
 * `nickname` is the name-shaped answer: same family name, and a first name that
 * is the other's short form. `same_employer` is the **same question with a
 * company attached** — everything `nickname` requires, and both of them on that
 * organisation's payroll or board — which is why it is probed first and sorted
 * to the top of the inbox. Both are **people only**, and both ask; neither
 * binds.
 *
 * The employer does not stand on its own (Phillips ruling, 2026-09-10). It
 * shipped meaning "two of one family name inside one organisation" and the first
 * report off prod returned 24 such pairs, 21 of them couples and relatives on
 * one payroll. A shared employer sharpens a question about a name; it never asks
 * one by itself.
 */
export const EntityMergeCandidateSignal = z.enum([
  'initials',
  'contains',
  'similar',
  'same_name',
  'nickname',
  'same_employer',
]);
export type EntityMergeCandidateSignal = z.infer<typeof EntityMergeCandidateSignal>;

export const EntityMergeCandidatePayload = z.object({
  kind: z.enum(['person', 'org', 'event']),
  /** The row that was just minted — the new spelling. */
  new_entity_id: Uuid,
  new_name: z.string(),
  /** The older live row it might be another spelling of. */
  candidate_entity_id: Uuid,
  candidate_name: z.string(),
  signal: EntityMergeCandidateSignal,
  /** The trigram similarity for `similar`; 1 for every other signal. */
  score: z.number(),
  /** One plain sentence, rendered verbatim: `"HBS" reads as the initials of …`. */
  reason: z.string(),
});
export type EntityMergeCandidatePayload = z.infer<typeof EntityMergeCandidatePayload>;

/**
 * **The two names, and who said so** — the `entity_rename` payload
 * (organisations-as-subjects note §2 chunk 1).
 *
 * `entity_id` is the row that was renamed and `subject_entity_id` on the
 * decision is the same id; it is on the payload as well because the ledger rows
 * keyed to this decision are read without the decision's own columns, and a
 * payload that names its subject is one join fewer for whatever is putting a
 * rename back.
 *
 * `entity_kind` is here for the same reason the merge candidate's is: a reader
 * of the log should not have to fetch the entity to know whether this was a
 * person, an organisation or an event.
 *
 * **`source` is the door, and it is what tells the two halves apart.**
 * `'office'` is somebody pressing Rename, which is filed already answered.
 * `'lookup'` is chunk 3's proposal from a run — "this looks like it is really
 * called Pogue Holdings" — which is filed *pending* and waits for a Yes or a No.
 * The row is otherwise identical, which is the point: both roads end in the same
 * rename and the same two ledger rows.
 */
export const ENTITY_RENAME_SOURCES = ['office', 'lookup'] as const;
export const EntityRenameSource = z.enum(ENTITY_RENAME_SOURCES);
export type EntityRenameSource = z.infer<typeof EntityRenameSource>;

export const EntityRenamePayload = z.object({
  entity_id: Uuid,
  entity_kind: z.enum(['person', 'org', 'event']),
  from_display_name: z.string(),
  to_display_name: z.string(),
  source: EntityRenameSource,
  /**
   * **What the run read the name off**, in the run's own words — one sentence,
   * and the whole of what makes the card answerable (§2 chunk 3a "the rename
   * proposal").
   *
   * "This looks like it is really called Pogue Holdings" is not a question
   * anybody can answer. "The site's footer reads *© 2026 Pogue Holdings LLC*" is.
   * The office is being asked to confirm a name for a record whose current
   * spelling is very often a placeholder somebody typed — "family business" —
   * so the evidence is not colour on the card, it is the card.
   *
   * **`.nullable()` and not `.optional()`** (the house rule): the office's own
   * door has no evidence to give, and that absence is a value the card renders
   * rather than a field that may be missing. Every `source: 'office'` row
   * carries `null` here.
   */
  evidence: z.string().nullable(),
  /**
   * **Why the office renamed it, in their own words** — null on the app's own
   * door, and the words the user agreed to on the connector's
   * (`docs/notes/2026-09-15-mcp-reconcile.md` §2.3).
   *
   * The connector requires a reason before it will rename anything, and the
   * ledger this decision hangs on has no column for one — `entity_merge_ledger`
   * is `{table, row, action, before}` and widening it to carry a sentence would
   * be widening the undo record to carry something undo does not read. The
   * payload is where a decision's own words already live, so it is where this
   * goes.
   *
   * **`.nullable()` and not `.optional()`** (the house rule): the knowledge
   * page's Rename asks for no reason, and that absence is a fact about the door
   * rather than a key a reader has to guess at. Every row written before
   * 2026-09-15 reads as `null` through the same schema.
   */
  reason: z.string().nullable(),
});
export type EntityRenamePayload = z.infer<typeof EntityRenamePayload>;

/**
 * **Yes, rename it** — or, from chunk 3's inbox card, no.
 *
 * A boolean rather than the `z.literal(true)` the office door alone would need,
 * because the *same* kind is what a lookup's proposal will be filed as, and the
 * answer a reader gives that card has two sides. Filing the office's own rename
 * as `{ confirmed: true }` today means the pending half can be built without a
 * second answer shape for one act, and without a log that reads two ways
 * depending on which door a rename came through.
 */
export const EntityRenameAnswer = z.object({ confirmed: z.boolean() }).strict();
export type EntityRenameAnswer = z.infer<typeof EntityRenameAnswer>;

/**
 * **Two records the office joined without being asked to** — the
 * `entity_merge_by_hand` payload (organisations-as-subjects note §2 chunk 1,
 * "Join by hand").
 *
 * The fold's own account of itself is the `entity_merge` decision this one names
 * in `merge_decision_id`: what moved, what collapsed, what the ledger holds. So
 * nothing here repeats any of it. What this payload carries is the part the
 * merge row cannot say — that **nobody asked**.
 *
 * `from_*` is the record folded away and `to_*` the one that stands, named the
 * way the merge names them so a reader moving between the two rows is not
 * translating. Both names are the names **as they were when the office pressed**;
 * the survivor's is what it is called afterwards, which on this door is the same
 * thing, because a join by hand never moves a name (the survivor keeps its own).
 *
 * The request body's `keep` — `'this'` or `'other'` — is not written down. It
 * only reads against the page the modal was opened from, which the row does not
 * carry, and the four absolute fields above already say which two records these
 * were and which one survived.
 */
export const EntityMergeByHandPayload = z.object({
  from_entity_id: Uuid,
  from_display_name: z.string(),
  to_entity_id: Uuid,
  to_display_name: z.string(),
  /**
   * The `entity_merge` row this press produced, so the log leads from the
   * gesture to the fold without anybody matching timestamps. Never null: this
   * row is written *after* the merge, in the merge's own transaction, and a join
   * that folded nothing files nothing.
   */
  merge_decision_id: Uuid,
});
export type EntityMergeByHandPayload = z.infer<typeof EntityMergeByHandPayload>;

/**
 * **Yes, join them** — and there is no other answer.
 *
 * `z.literal(true)` rather than {@link EntityRenameAnswer}'s boolean, because the
 * two kinds are shaped differently on purpose: a rename can be *proposed* by a
 * run and declined by the office, so its answer has two sides. Nothing proposes
 * a join by hand — that is what the merge card is for — so the row is only ever
 * written by somebody who did it, and a `false` here would be a record of a
 * fold that never happened.
 */
export const EntityMergeByHandAnswer = z.object({ confirmed: z.literal(true) }).strict();
export type EntityMergeByHandAnswer = z.infer<typeof EntityMergeByHandAnswer>;

/**
 * **What an office statement may state** (organisations-as-subjects note §2
 * chunk 2a, pass ii).
 *
 * Three of the five org anchor kinds and deliberately not the other two.
 * `registry_id` is a filing number nobody types from memory and chunk 3's lookup
 * is what will find it; `associated_person` is a person, and a statement that
 * confirmed one would be the office naming somebody through a door that files no
 * claim, no evidence and no held row — the §1.2 ruling ("never a person from a
 * note") reaching the anchor channel as well as the claim channel.
 *
 * `domain` arrives from the website field, which is a form control the office
 * fills in. `location` and `jurisdiction` arrive from the *text*, read by the
 * restricted extractor — which is why they are confirmed rather than candidate
 * anchors: the sentence they came out of was written by the office about their
 * own record, and the office is answerable for it in a way a fetched page's
 * author never is.
 */
export const OFFICE_STATEMENT_KINDS = ['domain', 'location', 'jurisdiction'] as const;
export const OfficeStatementKind = z.enum(OFFICE_STATEMENT_KINDS);
export type OfficeStatementKind = z.infer<typeof OfficeStatementKind>;

/**
 * **One office statement**, and the whole of what one row of it says: a kind and
 * the value as the statement settled it — the bare host for a `domain`, the words
 * the text used for the other two.
 */
export const OfficeStatement = z
  .object({
    kind: OfficeStatementKind,
    value: z.string().min(1),
  })
  .strict();
export type OfficeStatement = z.infer<typeof OfficeStatement>;

/**
 * The `office_statement` payload. `entity_id` is the organisation and the
 * decision's own `subject_entity_id` is the same id, for the reason
 * {@link EntityRenamePayload} carries its own: the rows keyed to this decision —
 * here, `anchor.source_decision_id` — are read without the decision's columns,
 * and a payload that names its subject is one join fewer.
 *
 * **`statements` may be empty.** The office pressed Add what you know, wrote a
 * paragraph, and the paragraph named no place and no jurisdiction. The prose is
 * the substance of what they did and it is on the record; this row is the log
 * saying they did it.
 */
export const OfficeStatementPayload = z.object({
  entity_id: Uuid,
  statements: z.array(OfficeStatement),
});
export type OfficeStatementPayload = z.infer<typeof OfficeStatementPayload>;

/**
 * **There is no No.** Unlike {@link EntityRenameAnswer}, this kind is never filed
 * pending and never becomes a card: the office is not being asked whether the
 * website is right, they are the ones saying it is. The answer exists because
 * `answered()` requires one — a row that carries a decider carries what they
 * decided — and the one thing it can say is that they said so.
 */
export const OfficeStatementAnswer = z.object({ confirmed: z.literal(true) }).strict();
export type OfficeStatementAnswer = z.infer<typeof OfficeStatementAnswer>;

/** A `profile_link` decision's payload — see the kind in `vocab.ts`. */
export const ProfileLinkPayload = z.object({
  entity_id: Uuid,
  anchor_id: Uuid,
  action: z.enum(PROFILE_LINK_ACTIONS),
  platform: z.enum(PROFILE_LINK_PLATFORMS),
  /** "@dhart_sf". */
  handle: z.string(),
});
export type ProfileLinkPayload = z.infer<typeof ProfileLinkPayload>;

/**
 * A `note_edit` or `note_removal` decision's payload — see the kinds in
 * `vocab.ts`. `removed_input_ids` is every row of the note (a long paste is
 * several); `filed_input_ids` is the edit's new rows and is empty on a removal.
 */
export const NoteChangePayload = z.object({
  entity_id: Uuid,
  removed_input_ids: z.array(Uuid).min(1),
  filed_input_ids: z.array(Uuid),
});
export type NoteChangePayload = z.infer<typeof NoteChangePayload>;

/* ————————————————————————— answers ————————————————————————— */

/**
 * "Matched 'Heritage'. Treat as Meritage Investment Group?" — one gesture
 * confirms the person and repairs the anchor. **One schema for both bind
 * answers**: the pick list's Confirm and the held list's shortcut are the same
 * act, and a near-match question the office answered on a held row must repair
 * the same anchor it would have repaired on a pick row.
 */
export const AnchorCorrections = z
  .array(
    z
      .object({
        original: z.string(),
        corrected: z.string(),
      })
      .strict(),
  )
  .optional();

/**
 * **One identity, or none** (spec §4.3). Keyed on `chosen_entity`, not
 * `chosen_candidate`, so it never matches the identity pick's arm first in the
 * answer union. "None of these" names what it declined, so a later run does not
 * offer the same identities again.
 */
export const OnchainEntityPickAnswer = z.union([
  z.object({ chosen_entity: z.string().min(1) }).strict(),
  z
    .object({ none_of_the_entities: z.literal(true), declined: z.array(z.string().min(1)) })
    .strict(),
]);
export type OnchainEntityPickAnswer = z.infer<typeof OnchainEntityPickAnswer>;

/** Two or more cards that are one person, each named once. */
const DistinctCandidateRefs = z
  .array(z.string().min(1))
  .min(2)
  .refine((refs) => new Set(refs).size === refs.length, {
    message: 'Each card can be chosen once.',
  });

/**
 * Three answers. `chosen_candidates` (2026-10-12) is **"these cards are one
 * person"**: the cross-source merge keeps two sources' views of one person apart
 * when nothing specific joins them (a records card with a birth date and a
 * spouse, a web card with an employer), and a single pick would anti-anchor the
 * other half of the person it bound. The office checks every card that is this
 * person; their anchors bind as one confirmed set, and only the unchecked cards
 * are anti-anchored. Two or more distinct refs: one is `chosen_candidate`.
 */
export const IdentityPickAnswer = z.union([
  z
    .object({
      chosen_candidate: z.string(),
      anchor_corrections: AnchorCorrections,
    })
    .strict(),
  z
    .object({
      chosen_candidates: DistinctCandidateRefs,
      anchor_corrections: AnchorCorrections,
    })
    .strict(),
  z.object({ none_of_these: z.literal(true) }).strict(),
]);
export type IdentityPickAnswer = z.infer<typeof IdentityPickAnswer>;

/**
 * Three answers, and **exactly one of them** — the union is of strict objects,
 * so a body carrying two arms' keys or none fails validation rather than being
 * quietly read as the first branch that matched.
 *
 * `{prose}` is the original: more detail, extracted server-side, the run
 * narrows and asks again. `{candidate_ref}` (2026-09-03) is the shortcut on an
 * `over_five` held list — "it's this one" — and it runs the **identity_pick
 * binding path** on that retained candidate: the same confirmed anchor set, the
 * same anti-anchors for the ones passed over, the same continuation. It is a
 * 400 `CANDIDATE_NOT_SELECTABLE` on a row whose `retained_candidates[]` does not
 * hold the ref, `over_fifteen` included. `{candidate_refs}` (2026-10-12) is the
 * pick list's same-person answer on the same retained list, under the same
 * refusals.
 */
export const IdentityMoreProseAnswer = z.union([
  z.object({ prose: z.string().min(1) }).strict(),
  z
    .object({
      candidate_ref: z.string().min(1),
      /** The pick arm's own field, on the pick arm's own schema (2026-09-03). */
      anchor_corrections: AnchorCorrections,
    })
    .strict(),
  /** The pick list's same-person answer on the held list (2026-10-12). */
  z
    .object({
      candidate_refs: DistinctCandidateRefs,
      anchor_corrections: AnchorCorrections,
    })
    .strict(),
]);
export type IdentityMoreProseAnswer = z.infer<typeof IdentityMoreProseAnswer>;

export const IdentityConfirmAnswer = z.union([
  z.object({ confirm: z.literal(true) }).strict(),
  z.object({ wrong_person: z.literal(true) }).strict(),
]);
export type IdentityConfirmAnswer = z.infer<typeof IdentityConfirmAnswer>;

/**
 * `{keep} | {leave_out} | {wrong_person}` written out (decided 2026-08-26) in
 * the sibling kinds' `{confirm: true}` style. `no_longer_true` is deliberately
 * absent — it exists only as a fact-level correction.
 */
export const ClaimAdjudicationAnswer = z.union([
  z.object({ keep: z.literal(true) }).strict(),
  z.object({ leave_out: z.literal(true) }).strict(),
  z.object({ wrong_person: z.literal(true) }).strict(),
]);
export type ClaimAdjudicationAnswer = z.infer<typeof ClaimAdjudicationAnswer>;

/**
 * **The relations the "are these the same?" card may write** (organisation-identity
 * note §1.3) — the three of {@link ORG_RELATIONS} that stand between two
 * *organisations*, and no more than that.
 *
 * The card starts from a pair the duplicate probe raised, and that pair is two
 * records of one kind. So the answer it can produce is a relation between two
 * organisations: `part_of` — the other is a campus, chapter, division or
 * subsidiary of the whole; `governed_by` — the whole is a board, regents or trustees
 * over the other; `affiliated_with` — a stated tie that is neither.
 *
 * **`hosted` is deliberately not here.** The catalog's fourth relation puts an
 * *event* in the counterparty slot, and there is no card on which two records the
 * probe called duplicates are an organisation and the event it put on — the
 * question was never asked about a pair like that. Narrowing the arm rather than
 * checking for it later is what makes the refusal a Zod boundary error with the
 * plain `VALIDATION_FAILED` sentence, instead of a rule somewhere in the answer
 * path that has to remember to run.
 */
export const MERGE_CARD_RELATIONS = [
  'part_of',
  'governed_by',
  'affiliated_with',
] as const satisfies readonly OrgRelation[];

/**
 * **The sentence the office reads when a card answer names `hosted`.** Written
 * here rather than left to Zod's own "invalid option" line, because this is one
 * of the few validation refusals a person can actually meet: the fourth relation
 * exists, it is on the fact type, and it is the one thing this answer cannot say.
 */
export const MERGE_CARD_RELATION_REFUSED =
  'An organization cannot host another organization. Say whether one is part of ' +
  'the other, governs it, or is affiliated with it.';

export const MergeCardRelation = z.enum(MERGE_CARD_RELATIONS, {
  error: MERGE_CARD_RELATION_REFUSED,
});
export type MergeCardRelation = z.infer<typeof MergeCardRelation>;

/**
 * `{verdict: 'same'}` joins the two records into one — permanently, through the
 * ordinary merge, which leaves a redirect so every later reference to the
 * merged-away spelling lands on the survivor. `{verdict: 'different'}` writes
 * the pair off, and the pair is never asked about again.
 *
 * **`{verdict: 'related'}` is the third answer** (organisation-identity note
 * §1.3), and it is the one the University of Texas cluster needed: neither one
 * record nor two strangers, but a campus and its system, or a system and its
 * board. It carries the relation and `whole_entity_id`, which names *which of
 * the pair's two organisations is the whole or the governing body* — the other
 * one is the smaller or governed side. The server writes one `org_relation` fact
 * that way round, records a `different` pair verdict so the question is never
 * asked again, and answers the decision with both words.
 *
 * It is legal on an organisation pair only. A pair of people or of events is
 * refused with a sentence; there is no such fact to write about them. The
 * relation is narrowed to {@link MERGE_CARD_RELATIONS} for the same reason:
 * `hosted` puts an event in the counterparty slot and is not a thing two
 * candidate duplicates can be to each other.
 *
 * **Discriminated on `verdict`, not a bare union**, so a body that names
 * `related` is measured against the `related` arm alone and the issue that comes
 * back is the one thing actually wrong with it. A bare union answers every
 * malformed body with `invalid_union` and "Invalid input", which is the right
 * answer to a client bug and the wrong one to a person who picked a relation
 * this card cannot write.
 *
 * Otherwise: what the answer *did* — which merge it produced, or why it produced
 * none — is written back onto the row by the server and read through
 * {@link EntityMergeCandidateRecordedAnswer}.
 */
export const EntityMergeCandidateAnswer = z.discriminatedUnion('verdict', [
  z
    .object({
      verdict: z.literal('same'),
      /**
       * **Which of the two names the one record keeps** (organisation-identity
       * note §4). The id of the record whose *name* stands, which need not be
       * the record that survives: the fold runs the way the merge gate says —
       * a tracked person never loses — and the name may have to come the other
       * way. Omitted means the survivor goes on being called what it was
       * called, which is what every answer before 2026-09-09 meant.
       *
       * A **request** field, so `.optional()` is right here: the office may say
       * nothing about the names, and a client that never asks (or a pair whose
       * two names are the same word) sends nothing. It must be one of the
       * question's own two ids; anything else is refused with a sentence.
       */
      keep_entity_id: Uuid.optional(),
    })
    .strict(),
  z.object({ verdict: z.literal('different') }).strict(),
  z
    .object({
      verdict: z.literal('related'),
      relation: MergeCardRelation,
      /** Which of the two candidates is the whole or the governing body. */
      whole_entity_id: Uuid,
    })
    .strict(),
]);
export type EntityMergeCandidateAnswer = z.infer<typeof EntityMergeCandidateAnswer>;

/** `POST /decisions/:id/answer` — the server dispatches on the row's kind. */
export const AnswerDecisionBody = z.union([
  IdentityPickAnswer,
  IdentityMoreProseAnswer,
  IdentityConfirmAnswer,
  ClaimAdjudicationAnswer,
  EntityMergeCandidateAnswer,
  /* The rename proposal's Yes / No. It joined this union when the lookup half
     shipped — before that the kind was never pending, so no client could post
     an answer to one. `{confirmed: boolean}` collides with no arm above: every
     other one is keyed on a literal-true flag or a verdict word. */
  EntityRenameAnswer,
  OnchainEntityPickAnswer,
]);
export type AnswerDecisionBody = z.infer<typeof AnswerDecisionBody>;

/**
 * **The likely-merge answer as it is stored** — the office's word, plus what the
 * server did with it (likely-merges note §1e).
 *
 * It is wider than what a client may post, on purpose. `merge_decision_id` names
 * the `entity_merge` row this answer produced, so the log leads from the
 * question to the fold without anybody having to match timestamps; it is `null`
 * on `different`, and on a `same` that merged nothing. `note` is the sentence
 * for exactly that case — the two had already been joined by the time the office
 * got here, or one of them is gone — and is `null` when the answer did what it
 * said.
 *
 * **`verdict` is nullable, and null means nobody answered.** A merge somewhere
 * else in the org can settle a question nobody had got to yet: fold two nodes
 * together and every pending question naming the merged-away one has no pair
 * left in it. Those are closed in the merge's own transaction with the `note`
 * that says which of the two things happened, and no verdict at all — writing
 * `same` there would put the office's word on a judgement the office never made.
 * `merge_decision_id` on one of those names the fold that closed it.
 *
 * The consequence, and it is the documented one: a second POST of the same
 * verdict does **not** replay 200 the way the other kinds do, because the
 * standing answer is no longer byte-identical to what was sent. It answers 409
 * `DECISION_ALREADY_ANSWERED` carrying this shape, which is what the card needs
 * anyway — the question is settled and the row is gone from the inbox.
 */
export const EntityMergeCandidateRecordedAnswer = z
  .object({
    verdict: z.enum(['same', 'different']).nullable(),
    merge_decision_id: Uuid.nullable(),
    note: z.string().nullable(),
  })
  .strict();
export type EntityMergeCandidateRecordedAnswer = z.infer<typeof EntityMergeCandidateRecordedAnswer>;

/**
 * **The `related` answer as it is stored** — its own shape rather than two more
 * nullable keys on the one above, for the reason the rest of this file writes
 * unions of strict objects: within an arm every field is *there*, so nothing has
 * to be read as "null because this was a different kind of answer".
 *
 * It is also what keeps every answer already on the record readable. The rows
 * the office answered before 2026-09-09 carry exactly three keys, and a widened
 * strict object would have stopped parsing all of them the day this shipped;
 * they go on matching the arm above, untouched.
 *
 * `merge_decision_id` is always `null` here — a `related` answer joins nothing —
 * and is carried so that one reader can take the merge id off any of these
 * without asking which arm it is holding. `relation` and `whole_entity_id` are
 * the office's own two words, kept so the card can render "X is part of Y" from
 * the answer alone.
 */
export const EntityMergeCandidateRelatedAnswer = z
  .object({
    verdict: z.literal('related'),
    merge_decision_id: z.null(),
    note: z.string().nullable(),
    relation: MergeCardRelation,
    /** Which of the pair is the whole or the governing body. */
    whole_entity_id: Uuid,
  })
  .strict();
export type EntityMergeCandidateRelatedAnswer = z.infer<typeof EntityMergeCandidateRelatedAnswer>;

/**
 * **A "Same" on which the office also chose the name** (organisation-identity
 * note §4) — its own arm, for the two reasons the `related` answer has one.
 *
 * Every `same` answer already on the record carries exactly three keys, and a
 * fourth one added to the arm above — `.nullable()` or not — would have stopped
 * all of them parsing the day this shipped. And within an arm every field is
 * *there*: a reader holding this one knows a name was chosen and what it was,
 * rather than reading a null and having to work out which of two things it meant.
 *
 * `kept_name` is the display name the one record carries now. `renamed` says
 * whether keeping it moved the name — false when the office picked the name the
 * surviving record already had, true when the survivor took the other one's and
 * left its old spelling behind as a redirect. Both are written from what the
 * merge actually did, not from what was asked for.
 */
export const EntityMergeCandidateKeptNameAnswer = z
  .object({
    verdict: z.literal('same'),
    merge_decision_id: Uuid.nullable(),
    note: z.string().nullable(),
    /** What the one record is called now. */
    kept_name: z.string(),
    /** Whether that renamed the surviving record. */
    renamed: z.boolean(),
  })
  .strict();
export type EntityMergeCandidateKeptNameAnswer = z.infer<typeof EntityMergeCandidateKeptNameAnswer>;

/**
 * The **server-only** recorded answer: written when a fact-level correction
 * auto-latches a pending non-blocking `claim_adjudication` row (server-design.md
 * §3.3). It is a fourth answer body that **no client may ever post** — it is
 * absent from `AnswerDecisionBody` on purpose — and the latched row carries the
 * correction's decision id as provenance.
 */
export const CorrectedAnswer = z.object({ corrected: z.literal(true) }).strict();
export type CorrectedAnswer = z.infer<typeof CorrectedAnswer>;

/**
 * What a decision row's `answer` may hold on read: any postable answer, or the
 * server-written `{corrected: true}`. Reads use this; writes use
 * `AnswerDecisionBody`.
 */
export const RecordedAnswer = z.union([
  AnswerDecisionBody,
  CorrectedAnswer,
  EntityMergeCandidateRecordedAnswer,
  EntityMergeCandidateRelatedAnswer,
  EntityMergeCandidateKeptNameAnswer,
]);
export type RecordedAnswer = z.infer<typeof RecordedAnswer>;

export const ANSWER_SCHEMA_BY_KIND = {
  identity_pick: IdentityPickAnswer,
  identity_more_prose: IdentityMoreProseAnswer,
  identity_confirm: IdentityConfirmAnswer,
  claim_adjudication: ClaimAdjudicationAnswer,
  entity_merge_candidate: EntityMergeCandidateAnswer,
  entity_rename: EntityRenameAnswer,
  onchain_entity_pick: OnchainEntityPickAnswer,
} as const;

/**
 * `DECISION_ALREADY_ANSWERED` carries the standing answer body, rendered in
 * place. It may be `{corrected: true}` — the row was latched by someone's
 * correction rather than answered directly.
 */
export const DecisionAlreadyAnsweredDetails = z.object({
  standing_answer: RecordedAnswer,
});
export type DecisionAlreadyAnsweredDetails = z.infer<typeof DecisionAlreadyAnsweredDetails>;

/* ————————————————————————— reads ————————————————————————— */

const decisionBase = {
  id: Uuid,
  status: DecisionStatus,
  subject_id: Uuid,
  run_id: Uuid.nullable(),
  blocking: z.boolean().nullable(),
  answer: RecordedAnswer.nullable(),
  answered_by: UserRef.nullable(),
  answered_at: Timestamp.nullable(),
  /**
   * **Which surface answered**, and the words the office gave for it
   * (reconcile-through-the-connector note §3).
   *
   * Null is the app, as every row before 2026-09-15. `'connector'` is an answer
   * typed through the MCP connector by the same member, under the same
   * `answered_by`; the mechanism is the same and only the hands differ.
   * `answer_reason` is the sentence the user agreed to, which the answer body
   * has no room for — every arm is `.strict()` and the latch compares the body
   * byte for byte, so a reason riding in the body would make a replay a
   * conflict. Null on an app answer, which asks for none.
   */
  answered_via: z.enum(['connector']).nullable(),
  answer_reason: z.string().nullable(),
  created_at: Timestamp,
};

/**
 * `GET /decisions/:id` — discriminated on `kind`. Only the four pending-answer
 * kinds are addressable: the route **404s on recorded (never-pending) kinds**
 * like `identity_bind` or `correction`, which are read through the decision log.
 */
export const DecisionDetail = z.discriminatedUnion('kind', [
  z.object({ ...decisionBase, kind: z.literal('identity_pick'), payload: IdentityPickPayload }),
  z.object({
    ...decisionBase,
    kind: z.literal('onchain_entity_pick'),
    payload: OnchainEntityPickPayload,
  }),
  z.object({
    ...decisionBase,
    kind: z.literal('identity_more_prose'),
    payload: IdentityMoreProsePayload,
  }),
  z.object({
    ...decisionBase,
    kind: z.literal('identity_confirm'),
    payload: IdentityConfirmPayload,
  }),
  z.object({
    ...decisionBase,
    kind: z.literal('claim_adjudication'),
    payload: ClaimAdjudicationPayload,
  }),
  z.object({
    ...decisionBase,
    kind: z.literal('entity_merge_candidate'),
    /**
     * **The stored payload plus one field the read composes.**
     *
     * `EntityMergeCandidatePayload` is what the likely-merge probe *writes* into
     * `decision.payload`, and `fold_readback` is never written: it is computed
     * on read, because the pair moves between the question being asked and
     * answered. Extending here rather than widening the stored shape is the
     * whole of that distinction — a required key on the stored contract would
     * refuse every row already on the board.
     */
    payload: EntityMergeCandidatePayload.extend({
      /**
       * **What answering "Same" would do, said before the office says it** — one
       * server-owned sentence, rendered verbatim, and the same sentence the
       * connector reads back (`docs/notes/2026-09-16-fold-tracked-people.md` §7).
       *
       * "Same person" used to be a bare yes about two spellings. Since 2026-09-16 a
       * fold carries a tracked record: it unbinds the losing side's identity,
       * supersedes its brief, moves its runs and its status, and changes which id
       * the one record lives at. None of that is on a card showing two names and a
       * reason, and none of it is something the office can be asked to agree to
       * without being told.
       *
       * *"Keeps Patrick Battle and its research; Pat Battle folds in; status becomes
       * member (the other record said prospect)."*
       *
       * Computed **on read**, not stored: the pair moves — a fold elsewhere, a run
       * finishing, a status switched — between the probe writing the question and
       * the office answering it, and a sentence frozen at write time would promise
       * whichever of those was true a week ago.
       *
       * **`.nullable()` and not `.optional()`** (the house rule): null is a value
       * the card renders — either side gone means there is nothing to say and the
       * answer road will record that instead of folding — not a key that may be
       * missing.
       */
      fold_readback: z.string().nullable(),
    }),
  }),
  z.object({
    ...decisionBase,
    kind: z.literal('entity_rename'),
    payload: EntityRenamePayload,
  }),
]);
export type DecisionDetail = z.infer<typeof DecisionDetail>;

/**
 * `POST /decisions/:id/answer` response. An identical repeat replays this
 * verbatim — except on `entity_merge_candidate`, whose stored answer is written
 * back with what the answer did and therefore answers 409 on a repeat (see
 * {@link EntityMergeCandidateRecordedAnswer}). `answer` is the **recorded**
 * shape for the same reason: it is what the row holds, not what was posted.
 */
export const AnswerDecisionResponse = z.object({
  decision: z.object({
    id: Uuid,
    kind: PendingDecisionKind,
    status: DecisionStatus,
    answer: RecordedAnswer,
    answered_by: UserRef.nullable(),
    answered_at: Timestamp,
  }),
});
export type AnswerDecisionResponse = z.infer<typeof AnswerDecisionResponse>;

/**
 * `POST /decisions/:id/needs-info` — one sentence saying what would settle an
 * open merge question, written against the row rather than answering it
 * (reconcile-through-the-connector note §2.4). Calling it again replaces the
 * sentence; answering the question is what clears it.
 */
export const DecisionNeedsInfoBody = z
  .object({
    text: z.string().trim().min(1).max(300),
  })
  .strict();
export type DecisionNeedsInfoBody = z.infer<typeof DecisionNeedsInfoBody>;

export const DecisionNeedsInfoResponse = z.object({
  decision_id: Uuid,
  needs_info: z.object({
    text: z.string(),
    by: UserRef.nullable(),
    via: z.enum(['connector']).nullable(),
    at: Timestamp,
  }),
});
export type DecisionNeedsInfoResponse = z.infer<typeof DecisionNeedsInfoResponse>;

/**
 * A pending row as the record composition carries it (drives the "2 of 3
 * reviewed" counter).
 *
 * **`entity_merge_candidate` is deliberately not one of these.** That counter
 * says how much of what research turned up on this person is still unread, and
 * a question about whether two organisations are one organisation is not a
 * finding about anybody. It reaches the office through the needs-action inbox
 * instead, which is the one surface that carries it.
 *
 * **`entity_rename` is out for the same reason** (§2 chunk 3a). A run proposing
 * that a record is really called something else is a question about the record's
 * *name*, not about anything research turned up on a person — and the record is
 * as often an organisation, which has no review counter at all. It rides the
 * inbox in its own list beside the merge candidates.
 */
export const DecisionSummary = z.object({
  id: Uuid,
  kind: PendingDecisionKind.exclude(['entity_merge_candidate', 'entity_rename']),
  /** `claim_adjudication` only; null on identity kinds. */
  blocking: z.boolean().nullable(),
  fact_lineage_id: Uuid.nullable(),
  run_id: Uuid.nullable(),
  created_at: Timestamp,
});
export type DecisionSummary = z.infer<typeof DecisionSummary>;

/**
 * `GET /subjects/:id/decisions` — the record's History disclosure: answered rows
 * and recorded events as one quiet audit list. `summary` is the server's
 * plain-language line for the row ("Kept a litigation finding", "Identified as
 * Daniel Voss") — the words negate / supersede / anti-anchor / lineage never
 * appear in it.
 */
export const DecisionLogRow = z.object({
  id: Uuid,
  kind: DecisionKind,
  status: DecisionStatus,
  actor: DecisionActor,
  answered_by: UserRef.nullable(),
  answered_at: Timestamp.nullable(),
  /**
   * **Which surface answered**, and the words the office gave for it
   * (reconcile-through-the-connector note §3).
   *
   * Null is the app, as every row before 2026-09-15. `'connector'` is an answer
   * typed through the MCP connector by the same member, under the same
   * `answered_by`; the mechanism is the same and only the hands differ.
   * `answer_reason` is the sentence the user agreed to, which the answer body
   * has no room for — every arm is `.strict()` and the latch compares the body
   * byte for byte, so a reason riding in the body would make a replay a
   * conflict. Null on an app answer, which asks for none.
   */
  answered_via: z.enum(['connector']).nullable(),
  answer_reason: z.string().nullable(),
  created_at: Timestamp,
  summary: z.string(),
});
export type DecisionLogRow = z.infer<typeof DecisionLogRow>;

export const DecisionLogResponse = z.object({
  entries: z.array(DecisionLogRow),
});
export type DecisionLogResponse = z.infer<typeof DecisionLogResponse>;
